import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SEO } from '@/components/SEO';
import { useAuth } from '@/context/AuthContext';
import { apiErrorMessage } from '@/lib/api';
import { LOGIN_PAGE, LOGIN_MFA } from '@/constants/testIds';

function LoginForm({ role, onSubmit }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSubmit(email.trim(), password);
    } catch (err) {
      setError(apiErrorMessage(err, 'Invalid email or password.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${role}-email`} className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">
          Email
        </Label>
        <Input
          id={`${role}-email`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid={LOGIN_PAGE.emailInput}
          className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-11 focus-visible:ring-[hsl(var(--lxra-gold))]"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${role}-password`} className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">
          Password
        </Label>
        <Input
          id={`${role}-password`}
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid={LOGIN_PAGE.passwordInput}
          className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-11 focus-visible:ring-[hsl(var(--lxra-gold))]"
        />
      </div>

      {error && (
        <p className="text-xs text-red-400" data-testid={LOGIN_PAGE.errorText}>
          {error}
        </p>
      )}

      <Button
        type="submit"
        disabled={loading}
        data-testid={LOGIN_PAGE.submitButton}
        className="w-full h-11 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
      </Button>
    </form>
  );
}

// Second step shown when the account has 2FA enabled. Accepts either a
// 6-digit authenticator code or an 8-character backup code (the backend
// checks both against the same field).
function MfaChallengeForm({ onVerify, onBack }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onVerify(code.trim());
    } catch (err) {
      setError(apiErrorMessage(err, 'Incorrect code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={LOGIN_MFA.section}>
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] mb-5">
        <ShieldCheck className="h-5 w-5" />
      </span>
      <h1 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] text-center">Two-Factor Verification</h1>
      <p data-testid={LOGIN_MFA.hint} className="mt-2 text-sm text-[hsl(var(--lxra-muted))] text-center">
        Enter the 6-digit code from your authenticator app, or one of your backup codes.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="mfa-code" className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">
            Verification Code
          </Label>
          <Input
            id="mfa-code"
            required
            autoFocus
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            data-testid={LOGIN_MFA.codeInput}
            className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-11 text-center tracking-[0.2em] focus-visible:ring-[hsl(var(--lxra-gold))]"
          />
        </div>

        {error && (
          <p className="text-xs text-red-400" data-testid={LOGIN_MFA.errorText}>
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={loading || !code.trim()}
          data-testid={LOGIN_MFA.submitButton}
          className="w-full h-11 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Sign In'}
        </Button>
      </form>

      <button
        type="button"
        onClick={onBack}
        data-testid={LOGIN_MFA.backLink}
        className="mt-6 w-full text-center text-xs text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors"
      >
        Back to sign in
      </button>
    </div>
  );
}

export default function Login() {
  const { loginAsAdmin, loginAsClient, resolveMfaChallenge } = useAuth();
  const navigate = useNavigate();
  const [mfaChallenge, setMfaChallenge] = useState(null); // { role: 'client'|'admin', token }

  const handleClientLogin = async (email, password) => {
    const data = await loginAsClient(email, password);
    if (data?.mfa_required) {
      setMfaChallenge({ role: 'client', token: data.challenge_token });
      return;
    }
    navigate('/client');
  };

  const handleAdminLogin = async (email, password) => {
    const data = await loginAsAdmin(email, password);
    if (data?.mfa_required) {
      setMfaChallenge({ role: 'admin', token: data.challenge_token });
      return;
    }
    navigate('/admin');
  };

  const handleMfaVerify = async (code) => {
    await resolveMfaChallenge(mfaChallenge.token, code);
    navigate(mfaChallenge.role === 'client' ? '/client' : '/admin');
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-[#0A0A0A] px-4 py-16" data-testid={LOGIN_PAGE.root}>
      <SEO title="Login" description="Client & studio login for LXRA Studios." />
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to site
        </Link>

        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8 sm:p-10">
          {mfaChallenge ? (
            <MfaChallengeForm onVerify={handleMfaVerify} onBack={() => setMfaChallenge(null)} />
          ) : (
            <>
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] mb-5">
                <Lock className="h-5 w-5" />
              </span>
              <h1 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] text-center">Sign In</h1>
              <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] text-center">
                Access your gallery or the studio dashboard.
              </p>

              <Tabs defaultValue="client" className="mt-8">
                <TabsList className="grid w-full grid-cols-2 bg-[#141416] border border-[hsl(var(--lxra-border))] h-10">
                  <TabsTrigger
                    value="client"
                    data-testid={LOGIN_PAGE.clientTab}
                    className="text-xs uppercase tracking-[0.08em] data-[state=active]:bg-[hsl(var(--lxra-gold))] data-[state=active]:text-[#0A0A0A] text-[hsl(var(--lxra-fg))]"
                  >
                    Client Login
                  </TabsTrigger>
                  <TabsTrigger
                    value="admin"
                    data-testid={LOGIN_PAGE.adminTab}
                    className="text-xs uppercase tracking-[0.08em] data-[state=active]:bg-[hsl(var(--lxra-gold))] data-[state=active]:text-[#0A0A0A] text-[hsl(var(--lxra-fg))]"
                  >
                    Studio Login
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="client" className="mt-6">
                  <LoginForm role="client" onSubmit={handleClientLogin} />
                  <p className="mt-5 text-center text-xs text-[hsl(var(--lxra-muted))]">
                    New client?{' '}
                    <Link to="/signup" data-testid={LOGIN_PAGE.clientSignupLink} className="text-[hsl(var(--lxra-gold))] hover:underline">
                      Create an account
                    </Link>
                  </p>
                </TabsContent>
                <TabsContent value="admin" className="mt-6">
                  <LoginForm role="admin" onSubmit={handleAdminLogin} />
                  <p className="mt-5 text-center text-xs text-[hsl(var(--lxra-muted))]">
                    Have a team invite link?{' '}
                    <Link to="/signup/photographer" data-testid={LOGIN_PAGE.photographerSignupLink} className="text-[hsl(var(--lxra-gold))] hover:underline">
                      Join the studio
                    </Link>
                  </p>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
